import {
  getPaginationRowModel,
  getFilteredRowModel,
  ColumnFiltersState,
  getSortedRowModel,
  getCoreRowModel,
  PaginationState,
  useReactTable,
  SortingState,
} from '@ui/components';
import {
  OpensearchSearchRequestOpenSearchDataTypeEnum,
  OpensearchSearchResponseListObject,
} from '@interfaces/models';
import { formatOpenSearchDate, getTableFilters, getTableSort } from '@shared/utils';
import { getOpensearchTableConfig, getOpensearchTableData } from '@api/opensearch';
import { useInfiniteQuery, useQueryClient, useQuery } from '@tanstack/react-query';
import { useDynamicFilters, useAutoColumns } from '@shared/components';
import { useCallback, useState, useMemo } from 'react';
import { useDebounce } from '@ui/hooks';

const initialPageParam = { pageSize: 50, scrollId: undefined };

export const useTransactionHistoryTable = ({
  pageSize = 50,
  dateFrom,
  dateTo,
}: {
  pageSize?: number;
  dateFrom?: Date;
  dateTo?: Date;
}) => {
  const queryClient = useQueryClient();

  const [tableSearchQuery, setTableSearchQuery] = useState('');
  const debouncedTableSearchQuery = useDebounce(tableSearchQuery, 800);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [rowSelection, setRowSelection] = useState({});
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: pageSize,
  });
  const [sorting, setSorting] = useState<SortingState>([]);

  const { data: tableConfig, isLoading: isTableConfigLoading } = useQuery({
    queryKey: ['transaction-history-table-config'],
    queryFn: () =>
      getOpensearchTableConfig(OpensearchSearchRequestOpenSearchDataTypeEnum.TRANSACTION_HISTORY),
  });

  const { columnDefinitions } = useAutoColumns({
    columns: tableConfig ?? [],
  });

  const dynamicFiltersData = useDynamicFilters({
    columns: tableConfig ?? [],
  });

  const queryParams = useMemo(
    () => ({
      sort: getTableSort(sorting),
      dateFrom: formatOpenSearchDate(dateFrom),
      dateTo: formatOpenSearchDate(dateTo),
      filters: getTableFilters(2, debouncedTableSearchQuery, {
        ...dynamicFiltersData.debouncedFiltersState,
      }),
    }),
    [
      dynamicFiltersData.debouncedFiltersState,
      debouncedTableSearchQuery,
      sorting,
      dateFrom,
      dateTo,
    ],
  );

  const {
    data: infiniteData,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isLoading,
    refetch: refetchTable,
  } = useInfiniteQuery({
    queryFn: ({ pageParam, signal }) => {
      return getOpensearchTableData(
        {
          openSearchDataType: OpensearchSearchRequestOpenSearchDataTypeEnum.TRANSACTION_HISTORY,
          ...queryParams,
          ...pageParam,
        },
        signal,
      );
    },
    getNextPageParam: (lastPage: OpensearchSearchResponseListObject) => {
      return {
        pageSize: pageSize,
        scrollId: lastPage.scrollId || undefined,
      };
    },
    queryKey: ['transaction-history-list', queryParams],
    initialPageParam: {
      pageSize: pageSize ?? initialPageParam.pageSize,
      scrollId: initialPageParam.scrollId,
    },
  });

  const data = useMemo(
    () => infiniteData?.pages.flatMap((page) => page.data) ?? [],
    [infiniteData],
  );

  const totalItems = useMemo(() => infiniteData?.pages[0]?.totalHits ?? 0, [infiniteData]);

  const loadedItems = useMemo(() => {
    return data.length;
  }, [data]);

  const table = useReactTable({
    data: data ?? [],
    columns: columnDefinitions,
    state: { pagination, sorting, globalFilter: tableSearchQuery, columnFilters, rowSelection },
    pageCount: Math.ceil(totalItems / pagination.pageSize),
    rowCount: totalItems,
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,
    getRowId: (row) => row[0] as string,
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    onGlobalFilterChange: setTableSearchQuery,
    onColumnFiltersChange: setColumnFilters,
    onRowSelectionChange: setRowSelection,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
  });

  const resetTable = useCallback(() => {
    queryClient.resetQueries({ queryKey: ['parking-spots-list'] });
  }, [queryClient]);

  return {
    globalFilter: table.getState().globalFilter,
    columnsLength: columnDefinitions.length,
    isTableConfigLoading,
    dynamicFiltersData,
    fetchNextPage,
    refetchTable,
    hasNextPage,
    loadedItems,
    resetTable,
    totalItems,
    isFetching,
    isLoading,
    table,
  };
};
